import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FiLock, FiSend } from 'react-icons/fi';
import { formsApi } from '../services/formsApi';
import type { FormPublicData, FormField, SubmitFormRequest, FormFieldType } from '@troia-v3/types';
import {
  EMPTY_PUBLIC_CONTACT,
  PublicContactFields,
  buildContactPayload,
  validatePublicContact,
  type PublicContactValues,
} from './PublicContactFields';

/** Campo que é pergunta (título e parágrafo não contam no progresso). */
const ehPergunta = (field: FormField): boolean =>
  field.type !== ('heading' as FormFieldType) && field.type !== ('paragraph' as FormFieldType);

const respondida = (value: string | string[] | number | boolean | null | undefined): boolean =>
  value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);

export const FormPublicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [form, setForm] = useState<FormPublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | string[] | number | boolean | null>>({});
  const [submitted, setSubmitted] = useState(false);
  // "Aceitar várias respostas" desligado e a pessoa já respondeu (409).
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  // Bloco "Seus dados" (2026-09-29) — só quando o formulário pede o contato.
  const [contact, setContact] = useState<PublicContactValues>(EMPTY_PUBLIC_CONTACT);
  const startTime = useRef(Date.now());

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    formsApi.getPublicForm(slug)
      .then(data => {
        setForm(data);
        startTime.current = Date.now();
      })
      .catch(err => {
        setError(err?.response?.data?.message || 'Formulário não encontrado');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const setAnswer = (fieldId: string, value: string | string[] | number | boolean | null) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !slug) return;

    // Validate required fields
    const contactRule = form.settings.contactData;
    const errors: Record<string, string> = contactRule ? validatePublicContact(contactRule, contact) : {};
    for (const field of form.fields) {
      if (field.type === ('heading' as FormFieldType) || field.type === ('paragraph' as FormFieldType)) continue;
      if (field.required) {
        const val = answers[field.id];
        if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
          errors[field.id] = 'Este campo é obrigatório';
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const completionTimeSeconds = Math.round((Date.now() - startTime.current) / 1000);
      const data: SubmitFormRequest = {
        answers: Object.entries(answers)
          .filter(([, v]) => v !== undefined)
          .map(([fieldId, value]) => ({ fieldId, value })),
        metadata: { completionTimeSeconds },
        ...(contactRule ? { contact: buildContactPayload(contact) } : {}),
      };
      await formsApi.submitPublicForm(slug, data);
      setSubmitted(true);
    } catch (err: unknown) {
      // 422 do backend traz o motivo por campo (`contact.phone`, …).
      const campos = (err as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } })?.response;
      if (campos?.status === 409) {
        setAlreadyAnswered(true);
        return;
      }
      const porCampo = campos?.status === 422 && campos.data?.errors
        ? Object.fromEntries(Object.entries(campos.data.errors).map(([campo, msgs]) => [campo, msgs[0] ?? 'Campo inválido']))
        : {};
      setValidationErrors(Object.keys(porCampo).length > 0 ? porCampo : { _form: 'Erro ao enviar formulário. Tente novamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-gray-500">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Formulário indisponível</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!form) return null;

  if (!form.isAcceptingResponses) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">{form.name}</h1>
          <p className="text-gray-500">
            {form.isExpired ? 'Este formulário expirou.' : 'Este formulário atingiu o limite máximo de respostas.'}
          </p>
        </div>
      </div>
    );
  }

  if (alreadyAnswered) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: form.styling.backgroundColor || '#f9fafb' }} data-testid="form-public-already-answered">
        <div className="text-center max-w-md px-4">
          <h1 className="text-xl font-semibold text-gray-800 mb-2">{form.name}</h1>
          <p className="text-gray-600">Você já respondeu este formulário. Obrigado!</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: form.styling.backgroundColor || '#f9fafb' }}>
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: form.styling.primaryColor || '#8b5cf6' }}>
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Obrigado!</h1>
          <p className="text-gray-600">
            {form.thankYouMessage || 'Sua resposta foi registrada com sucesso.'}
          </p>
        </div>
      </div>
    );
  }

  const primaryColor = form.styling.primaryColor || '#8b5cf6';
  // Barra de progresso (2026-09-30): quantas perguntas já têm resposta.
  const perguntas = form.fields.filter(ehPergunta);
  const respondidas = perguntas.filter((f) => respondida(answers[f.id])).length;

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: form.styling.backgroundColor || '#f9fafb' }}>
      {form.settings.showProgressBar && perguntas.length > 0 && (
        <div className="sticky top-0 z-10 -mt-8 -mx-4 mb-6 px-4 py-3 bg-white/95 backdrop-blur border-b border-gray-200" data-testid="form-public-progress">
          <div className="max-w-xl mx-auto flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.round((respondidas / perguntas.length) * 100)}%`, backgroundColor: primaryColor }}
              />
            </div>
            <span className="text-xs font-semibold text-gray-600 tabular-nums" data-testid="form-public-progress-text">
              {respondidas} de {perguntas.length}
            </span>
          </div>
        </div>
      )}
      <div className="max-w-xl mx-auto">
        {/* Brandbar (mockup .brandbar): marca quadrada + nome + subtítulo, acima do hero */}
        <div className="flex items-center gap-[11px] mb-[22px]">
          <span
            className="flex items-center justify-center flex-shrink-0 overflow-hidden"
            style={{ width: 42, height: 42, borderRadius: 11, backgroundColor: primaryColor, color: '#fff', fontWeight: 900, fontSize: 20 }}
          >
            {form.styling.logoUrl
              ? <img src={form.styling.logoUrl} alt="" className="w-full h-full object-cover" />
              : (form.name?.[0] || 'F').toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="font-bold text-[16px] tracking-tight text-gray-900 truncate">{form.name}</div>
            <div className="text-xs text-gray-500">Formulário</div>
          </div>
        </div>

        {/* Cover (opcional) */}
        {form.styling.coverUrl && (
          <div className="h-40 rounded-t-xl bg-cover bg-center" style={{ backgroundImage: `url(${form.styling.coverUrl})` }} />
        )}
        {/* Hero (descrição) — colado ao card; o nome do form vive na brandbar acima */}
        {(form.description || form.styling.coverUrl) && (
          <div className={`bg-white shadow-sm border border-gray-200 border-b-0 px-[26px] pt-[22px] pb-5 ${form.styling.coverUrl ? '' : 'rounded-t-xl'}`}>
            {form.description && (
              <p className="text-[15px] text-gray-600 leading-relaxed">{form.description}</p>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Card único com todos os campos, colado ao hero */}
          <div className={`bg-white shadow-sm border border-gray-200 p-6 space-y-6 ${(form.description || form.styling.coverUrl) ? 'rounded-b-xl border-t-0' : 'rounded-xl'}`}>
            {validationErrors._form && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{validationErrors._form}</p>
              </div>
            )}
            {form.settings.contactData && (
              <PublicContactFields
                rule={form.settings.contactData}
                value={contact}
                onChange={(next) => {
                  setContact(next);
                  setValidationErrors((prev) => {
                    const rest = { ...prev };
                    delete rest['contact.name'];
                    delete rest['contact.phone'];
                    delete rest['contact.email'];
                    return rest;
                  });
                }}
                errors={validationErrors}
                primaryColor={primaryColor}
              />
            )}
            {form.fields.map((field) => (
              <PublicField
                key={field.id}
                field={field}
                value={answers[field.id]}
                onChange={(v) => setAnswer(field.id, v)}
                error={validationErrors[field.id]}
                primaryColor={primaryColor}
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-[50px] mt-4 rounded-xl text-white text-base font-bold flex items-center justify-center gap-2 transition-opacity"
            style={{ backgroundColor: primaryColor, opacity: submitting ? 0.7 : 1 }}
          >
            <FiSend className="w-[18px] h-[18px]" />
            {submitting ? 'Enviando...' : 'Enviar'}
          </button>

          {/* Powered footer (mockup: .powered) */}
          <div className="mt-[22px] flex items-center justify-center gap-1.5 text-xs text-gray-400">
            <FiLock className="w-3.5 h-3.5" />
            Seguro · feito com <b className="text-gray-500">Troia Chat</b>
          </div>
        </form>
      </div>
    </div>
  );
};

const PublicField: React.FC<{
  field: FormField;
  value: string | string[] | number | boolean | null | undefined;
  onChange: (value: string | string[] | number | boolean | null) => void;
  error?: string;
  primaryColor: string;
}> = ({ field, value, onChange, error, primaryColor }) => {
  if (field.type === ('heading' as FormFieldType)) {
    return <h2 className="text-lg font-semibold text-gray-900">{field.label}</h2>;
  }

  if (field.type === ('paragraph' as FormFieldType)) {
    return <p className="text-sm text-gray-600">{field.label}</p>;
  }

  return (
    <div>
      <label className="block text-[13px] font-semibold text-gray-800 mb-[7px]">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {field.description && (
        <p className="text-xs text-gray-500 mb-2">{field.description}</p>
      )}

      {/* Text inputs */}
      {(['short_text', 'email', 'phone', 'url', 'cpf', 'cnpj', 'number'] as FormFieldType[]).includes(field.type) && (
        <input
          type={field.type === ('email' as FormFieldType) ? 'email' : field.type === ('number' as FormFieldType) ? 'number' : 'text'}
          value={String(value ?? '')}
          onChange={(e) => onChange(field.type === ('number' as FormFieldType) ? Number(e.target.value) : e.target.value)}
          placeholder={field.placeholder}
          className="w-full min-h-[46px] border border-gray-300 rounded-[10px] px-[14px] py-3 text-[15px] focus:outline-none"
          onFocus={(e) => { e.currentTarget.style.borderColor = primaryColor; e.currentTarget.style.boxShadow = `0 0 0 3px ${primaryColor}1a`; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; }}
        />
      )}

      {field.type === ('long_text' as FormFieldType) && (
        <textarea
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={4}
          className="w-full border border-gray-300 rounded-[10px] px-[14px] py-3 text-[15px] focus:outline-none resize-none"
          onFocus={(e) => { e.currentTarget.style.borderColor = primaryColor; e.currentTarget.style.boxShadow = `0 0 0 3px ${primaryColor}1a`; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; }}
        />
      )}

      {field.type === ('date' as FormFieldType) && (
        <input
          type="date"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="w-full min-h-[46px] border border-gray-300 rounded-[10px] px-[14px] py-3 text-[15px]"
        />
      )}

      {field.type === ('time' as FormFieldType) && (
        <input
          type="time"
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="w-full min-h-[46px] border border-gray-300 rounded-[10px] px-[14px] py-3 text-[15px]"
        />
      )}

      {/* Select */}
      {field.type === ('select' as FormFieldType) && (
        <select
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="w-full min-h-[46px] border border-gray-300 rounded-[10px] px-[14px] py-3 text-[15px]"
        >
          <option value="">Selecione...</option>
          {field.options?.map(opt => (
            <option key={opt.id} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}

      {/* Radio */}
      {field.type === ('radio' as FormFieldType) && (
        <div className="flex flex-col gap-[9px]">
          {field.options?.map(opt => {
            const isChecked = value === opt.value;
            return (
              <label
                key={opt.id}
                className="flex items-center gap-[10px] px-[14px] py-3 rounded-[10px] border cursor-pointer text-sm text-gray-700 transition-colors"
                style={{
                  borderColor: isChecked ? primaryColor : '#d1d5db',
                  backgroundColor: isChecked ? `${primaryColor}1a` : 'transparent',
                }}
                onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.backgroundColor = `${primaryColor}1a`; }}
                onMouseLeave={(e) => { if (!isChecked) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <input
                  type="radio"
                  name={field.id}
                  value={opt.value}
                  checked={isChecked}
                  onChange={() => onChange(opt.value)}
                  className="hidden"
                />
                <span
                  className="w-[18px] h-[18px] rounded-full border-2 flex-shrink-0"
                  style={{
                    borderColor: isChecked ? primaryColor : '#d1d5db',
                    boxShadow: isChecked ? `inset 0 0 0 4px ${primaryColor}` : 'none',
                  }}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* Checkbox */}
      {(field.type === ('checkbox' as FormFieldType) || field.type === ('multi_select' as FormFieldType)) && (
        <div className="space-y-2">
          {field.options?.map(opt => {
            const selected = Array.isArray(value) ? value.includes(opt.value) : false;
            return (
              <label key={opt.id} className="flex items-center gap-[10px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => {
                    const current = Array.isArray(value) ? value : [];
                    const next = selected ? current.filter(v => v !== opt.value) : [...current, opt.value];
                    onChange(next);
                  }}
                  className="hidden"
                />
                <span
                  className="w-[18px] h-[18px] rounded-[5px] border-2 flex items-center justify-center flex-shrink-0"
                  style={{
                    borderColor: selected ? primaryColor : '#d1d5db',
                    backgroundColor: selected ? primaryColor : 'transparent',
                    color: '#ffffff',
                  }}
                >
                  {selected && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* File upload (mockup: dropzone). Captura o nome do arquivo no value;
          persistência do binário depende de endpoint de upload no backend. */}
      {field.type === ('file_upload' as FormFieldType) && (
        <label
          className="flex flex-col items-center justify-center gap-1 w-full border-2 border-dashed rounded-lg px-3 py-6 cursor-pointer text-center transition-colors"
          style={{ borderColor: '#d1d5db' }}
        >
          <span className="text-sm font-medium" style={{ color: primaryColor }}>
            {value ? String(value) : 'Clique para anexar um arquivo'}
          </span>
          <span className="text-xs text-gray-500">ou arraste e solte aqui</span>
          <input
            type="file"
            className="hidden"
            onChange={(e) => onChange(e.target.files?.[0]?.name ?? null)}
          />
        </label>
      )}

      {/* Rating */}
      {field.type === ('rating' as FormFieldType) && (
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              className="text-2xl transition-colors"
              style={{ color: (value as number) >= star ? '#f59e0b' : '#d1d5db' }}
            >
              ★
            </button>
          ))}
        </div>
      )}

      {/* Linear Scale */}
      {field.type === ('linear_scale' as FormFieldType) && (
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
            <button
              key={num}
              type="button"
              onClick={() => onChange(num)}
              className="w-8 h-8 rounded-full text-sm font-medium transition-colors border"
              style={{
                backgroundColor: value === num ? primaryColor : 'transparent',
                color: value === num ? '#ffffff' : '#6b7280',
                borderColor: value === num ? primaryColor : '#d1d5db',
              }}
            >
              {num}
            </button>
          ))}
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};
